import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { FileText, Eye, EyeOff } from 'lucide-react';
import api from '@/utils/api';

const GlassInput = ({ id, type, placeholder, value, onChange, required }) => {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div className="relative">
      <input
        id={id}
        type={isPassword && show ? 'text' : type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/40 outline-none transition-all duration-200 focus:ring-2 pr-10"
        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)' }}
        onFocus={e => { e.target.style.border = '1px solid rgba(37,99,235,0.7)'; e.target.style.background = 'rgba(255,255,255,0.1)'; }}
        onBlur={e => { e.target.style.border = '1px solid rgba(255,255,255,0.15)'; e.target.style.background = 'rgba(255,255,255,0.07)'; }}
      />
      {isPassword && (
        <button type="button" tabIndex={-1} onClick={() => setShow(s => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
};

const GlassLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block text-xs font-medium text-white/60 mb-1.5 uppercase tracking-wider">
    {children}
  </label>
);

const GradientButton = ({ children, disabled, type = 'button', onClick }) => (
  <button
    type={type}
    disabled={disabled}
    onClick={onClick}
    style={{
      background: disabled
        ? 'rgba(255,255,255,0.12)'
        : 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #0ea5e9 100%)',
    }}
    className="w-full py-3 px-6 rounded-xl text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 shadow-lg"
  >
    {children}
  </button>
);

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error('Geçersiz sıfırlama linki.'); return; }
    if (password !== confirm) { toast.error('Şifreler eşleşmiyor.'); return; }
    if (password.length < 6) { toast.error('Şifre en az 6 karakter olmalıdır.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      toast.success('Şifreniz başarıyla güncellendi!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Bir hata oluştu. Link geçersiz veya süresi dolmuş olabilir.';
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(160deg, #060d1a 0%, #0a1628 45%, #0d1f35 100%)' }}
    >
      {/* Background grid */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="rp-grid" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(59,130,246,0.07)" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#rp-grid)"/>
      </svg>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-15 blur-3xl"
          style={{ background: 'radial-gradient(circle, #1d4ed8, transparent)' }} />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full opacity-15 blur-3xl"
          style={{ background: 'radial-gradient(circle, #0ea5e9, transparent)' }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-2xl"
              style={{ background: 'linear-gradient(135deg, #1d4ed8, #0ea5e9)' }}>
              <FileText className="h-8 w-8 text-white" />
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-white mb-1 tracking-tight">UzmanRapor</h1>
          <p className="text-white/50 text-sm">Şifre Sıfırlama</p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-7 shadow-2xl"
          style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          {!token ? (
            <div className="text-center py-4">
              <p className="text-red-400 text-sm mb-4">Geçersiz veya eksik sıfırlama linki.</p>
              <Link to="/login" className="text-blue-400 hover:text-blue-300 text-sm transition-colors">
                Giriş sayfasına dön
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <svg className="w-7 h-7 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Şifreniz Güncellendi!</h3>
              <p className="text-white/50 text-sm mb-6">Yeni şifrenizle giriş yapabilirsiniz.</p>
              <GradientButton onClick={() => navigate('/login')}>
                Giriş Yap
              </GradientButton>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="mb-2">
                <h3 className="text-white font-semibold text-base">Yeni Şifre Belirleyin</h3>
                <p className="text-white/50 text-xs mt-1">En az 6 karakter olmalıdır.</p>
              </div>
              <div>
                <GlassLabel htmlFor="new-password">Yeni Şifre</GlassLabel>
                <GlassInput
                  id="new-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div>
                <GlassLabel htmlFor="confirm-password">Şifre Tekrar</GlassLabel>
                <GlassInput
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>
              <div className="pt-2">
                <GradientButton type="submit" disabled={loading}>
                  {loading ? 'Güncelleniyor...' : 'Şifremi Güncelle'}
                </GradientButton>
              </div>
              <p className="text-center">
                <Link to="/login" className="text-xs text-white/40 hover:text-white/60 transition-colors">
                  Giriş sayfasına dön
                </Link>
              </p>
            </form>
          )}
        </div>

        <p className="text-center text-white/20 text-xs mt-6">
          © {new Date().getFullYear()} uzmanrapor.com
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
